import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (username: string, email: string, password: string) =>
    api.post('/auth/register', { username, email, password }),
  getProfile: () => api.get('/auth/profile')
};

// Casino Groups
export const casinoGroupApi = {
  getAll: () => api.get('/casinos/groups'),
  create: (name: string) => api.post('/casinos/groups', { name }),
  delete: (id: number) => api.delete(`/casinos/groups/${id}`)
};

// Casinos
export const casinoApi = {
  getAll: () => api.get('/casinos'),
  getById: (id: number) => api.get(`/casinos/${id}`),
  create: (data: { name: string; url?: string; logo_url?: string; group_id?: number }) =>
    api.post('/casinos', data),
  update: (id: number, data: { name: string; url?: string; logo_url?: string; group_id?: number }) =>
    api.put(`/casinos/${id}`, data),
  delete: (id: number) => api.delete(`/casinos/${id}`)
};

// Transactions
export const transactionApi = {
  getAll: (params?: {
    startDate?: string;
    endDate?: string;
    casinoId?: number;
    type?: string;
  }) => api.get('/transactions', { params }),
  create: (data: {
    casino_id: number;
    type: 'deposit' | 'withdrawal';
    amount: number;
    note?: string;
    transaction_date: string;
  }) => api.post('/transactions', data),
  update: (id: number, data: {
    casino_id: number;
    type: 'deposit' | 'withdrawal';
    amount: number;
    note?: string;
    transaction_date: string;
  }) => api.put(`/transactions/${id}`, data),
  delete: (id: number) => api.delete(`/transactions/${id}`)
};

// Stats
export const statsApi = {
  getOverall: () => api.get('/stats/overall'),
  getMonthly: (year?: number) => api.get('/stats/monthly', { params: { year } }),
  getMonthlyCumulative: (year?: number) => api.get('/stats/monthly-cumulative', { params: { year } }),
  getDaily: (year?: number, month?: number) => api.get('/stats/daily', { params: { year, month } }),
  getYearly: () => api.get('/stats/yearly'),
  getCasinos: () => api.get('/stats/casinos'),
  getGroups: () => api.get('/stats/groups'),
  getWeekdays: () => api.get('/stats/weekdays'),
  getAvailableYears: () => api.get('/stats/years'),
  getAvailableMonths: (year?: number) => api.get('/stats/months', { params: { year } })
};

// Bonushunts
export const bonushuntApi = {
  getAll: (status?: string) => api.get('/bonushunts', { params: { status } }),
  getOne: (id: number) => api.get(`/bonushunts/${id}`),
  create: (data: { name?: string; hunt_date: string; start_balance?: number }) =>
    api.post('/bonushunts', data),
  update: (id: number, data: { name?: string; hunt_date: string; start_balance?: number; status?: string }) =>
    api.put(`/bonushunts/${id}`, data),
  delete: (id: number) => api.delete(`/bonushunts/${id}`),
  addEntry: (huntId: number, data: { slot_name: string; bet_amount: number }) =>
    api.post(`/bonushunts/${huntId}/entries`, data),
  updateEntry: (huntId: number, entryId: number, data: {
    slot_name: string;
    bet_amount: number;
    win_amount?: number;
    opened?: boolean;
  }) => api.put(`/bonushunts/${huntId}/entries/${entryId}`, data),
  deleteEntry: (huntId: number, entryId: number) =>
    api.delete(`/bonushunts/${huntId}/entries/${entryId}`)
};

// Big Wins
export interface BigWinPayload {
  slot_name: string;
  provider?: string;
  casino_id?: number | null;
  bet_amount: number;
  win_amount: number;
  win_date: string;
  note?: string;
  // Data-URL beim Hochladen, null zum Entfernen, undefined laesst das Bild unveraendert.
  image?: string | null;
}

export const bigwinApi = {
  getAll: (sort?: string) => api.get('/bigwins', { params: { sort } }),
  getStats: () => api.get('/bigwins/stats'),
  getSlots: () => api.get('/bigwins/slots'),
  create: (data: BigWinPayload) => api.post('/bigwins', data),
  update: (id: number, data: BigWinPayload) => api.put(`/bigwins/${id}`, data),
  delete: (id: number) => api.delete(`/bigwins/${id}`),
  // Bilder sind privat und erfordern den Auth-Header, daher als Blob laden.
  getImage: (url: string) => api.get(url.replace(/^\/api/, ''), { responseType: 'blob' })
};

export default api;
